import DeleteIcon from '@mui/icons-material/Delete';
import { CircularProgress, Paper, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link as NavLink, Route, Switch } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
// import { DeleteReusableModal } from '../components/modals/DeleteReusableModal';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import { green } from '@mui/material/colors';
import clsx from 'clsx';
import { useMutation } from 'react-query';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { trackAddConference } from '../../common/userTracking';
import { makePHPRequestPayload } from '../../recoil/atoms/makePHPRequestPayload';
import { user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { ConferenceModal } from '../components/modals/ConferenceModal';
import { DeleteConferenceModal } from '../components/modals/DeleteConferenceModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import { DataTable } from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

export function ManageConferences({ history }) {
  // state for opening/closing delete conference modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state for add new modal
  const [selected, setSelected] = useState([]);
  const [deleteGroupId, setDeleteGroupId] = useState('');
  const [, setDeleteCancelTokenSource] = useState();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  // used to get the data
  // const getBusinessConferences = useRecoilValueLoadable(
  //   makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_CONFERENCES })
  // );
  // // refetch toggle to refresh data manully upon invocation
  const setRefetchToggleForBusinessConferences = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_CONFERENCES)
  );
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const data = {
    call: methodNameEnum.GET_BUSINESS_CONFERENCES,
  };
  const getBusinessConferences = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_CONFERENCES,
      payloadJsonString: JSON.stringify(data),
    })
  );

  const conferencesCached = useRef([]);
  conferencesCached.current = useMemo(
    () =>
      getBusinessConferences?.state === 'hasValue'
        ? getBusinessConferences.contents?.response?.data?.result?.map((o) => ({
            ...o,
            id: o.conferenceid,
          })) ?? []
        : conferencesCached.current,
    [getBusinessConferences]
  );
  const useStyles = makeStyles((theme) => ({
    messageColumn: {
      width: '100% !important',
      minWidth: '400px !important',
      lineHeight: 'normal !important',
      whiteSpace: 'normal !important',
      padding: theme.spacing(1) + 'px !important',
    },
    buttonProgress: {
      color: green[500],
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -22,
      marginLeft: -22,
    },
    deleteIconButton: {
      color: theme.custom.button.delete.main,
    },
    spinner: {
      position: 'fixed !important',
    },
    audio: {
      transition: 'all 0.5s linear',
      boxShadow: '0px 0px 0px 0px',
      backgroundColor: '#f0f3f4',
      borderRadius: '7px 7px 7px 7px',
      height: 32,
      margin: '10px 0px',
      '&:focus': {
        outline: 0,
      },
    },
    actionColumn: {
      alignItems: 'center',
      justifyContent: 'center',
    },
  }));
  const classes = useStyles();
  const mutation = useMutation(
    getApiMutation(userAtom.contents, setUserAtom, setDeleteCancelTokenSource)
  );
  function formatter(params) {
    return deleteGroupId === params.row.id ? (
      <div>
        <article className={clsx(classes.actionColumn, 'row')}>
          <Tooltip title="Confirm Deletion">
            <IconButton
              aria-label="accept"
              onClick={async (event) => {
                event.preventDefault(); // stop link
                const data = {
                  call: methodNameEnum.DELETE_CONFERENCE,
                  apikey: userAtom.contents.apikey,
                  businessid: userAtom.contents.businessid,
                  conferenceid: params.row.id,
                };
                const response = await mutation.mutateAsync({
                  endpoint: endpointEnum.DELETE_CONFERENCE,
                  payload: data,
                });
                setRefetchToggleForBusinessConferences({});
                if (response?.data?.success) {
                  enqueueSnackbarWithStyles({
                    message: `Successfully Deleted ${params.row.name} Conference`,
                    alertProps: { severity: 'success' },
                  });
                } else {
                  enqueueSnackbarWithStyles({
                    message: `Encountered an error deleting the ${params.row.name} conference`,
                    alertProps: { severity: 'warning' },
                  });
                }
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <CheckIcon color={mutation.isLoading ? 'disabled' : 'primary'} />
              {mutation.isLoading && (
                <CircularProgress
                  size={43}
                  className={classes.buttonProgress}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              aria-label="cancel"
              onClick={(event) => {
                event.preventDefault(); // stop link
                // reset groupid hide confirm / cancel deletion
                setDeleteGroupId(undefined);
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <ClearIcon
                color="disabled"
                className={clsx({
                  [classes.deleteIconButton]: !mutation.isLoading, // || deleteCancelTokenSource,
                })}
              />
            </IconButton>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div className={clsx(classes.actionColumn, 'row')}>
        <NavLink to={`/manage/conferences/${params.value}/edit`}>
          <Tooltip title="Edit">
            <IconButton name="edit" size="large">
              <CreateIcon color="primary" />
            </IconButton>
          </Tooltip>
        </NavLink>
        <Tooltip title="Delete">
          <IconButton
            aria-label="delete"
            onClick={() => {
              setDeleteGroupId(params.row.id);
            }}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  const readOnly = useBusinessPermissions().accountReadOnly;
  function nameFormatter(params) {
    return (
      <Tooltip title="Edit">
        <NavLink to={`/manage/conferences/${params.row.conferenceid}/edit`}>
          {params.value}
        </NavLink>
      </Tooltip>
    );
  }
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: nameFormatter,
    },
    {
      field: 'conferenceid',
      headerName: 'Actions',
      hide: readOnly,
      disableExport: true,
      disableSearch: true, // do not search field content
      disableRowSelection: true, // to have onlcick to work row selection should be disabled
      enableSorting: false,
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: formatter,
    },
  ];
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Conferences"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            numSelected={selected.length}
            onClickForAdd={() => {
              history.push('/manage/conferences/add');
              trackAddConference();
            }}
            onClickForDelete={() => {
              handleShow();
            }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    [handleShow, selected.length, readOnly]
  );
  const [sortModel, setGridSortModel] = React.useState([
    { field: 'name', sort: 'asc' },
  ]);
  // console.log(rows, 'rows in conf');
  // console.log(conferencesCached.current, 'cached conf');
  return (
    <Paper elevation={0}>
      <Switch>
        <Route path="/manage/conferences/:id/edit">
          {({ history, match }) => (
            <ConferenceModal
              show={match != null}
              id={match?.params.id}
              handleClose={() => {
                history.push('/manage/conferences');
              }}
              classes={classes}
              makePHPRequestPayload={makePHPRequestPayload}
              methodNameEnum={methodNameEnum}
              setRefetchToggleForBusinessConferences={
                setRefetchToggleForBusinessConferences
              }
              enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
              conferences={getBusinessConferences}
            />
          )}
        </Route>
        <Route path="/manage/conferences/add">
          {({ history, match }) => (
            <ConferenceModal
              id={undefined}
              show={match != null}
              handleClose={() => {
                history.push('/manage/conferences');
              }}
              classes={classes}
              makePHPRequestPayload={makePHPRequestPayload}
              methodNameEnum={methodNameEnum}
              setRefetchToggleForBusinessConferences={
                setRefetchToggleForBusinessConferences
              }
              enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
              conferences={getBusinessConferences}
            />
          )}
        </Route>
      </Switch>
      <div>
        <DataTable
          enableSelectAll={true}
          setSelected={setSelected}
          rows={conferencesCached?.current ?? []}
          loading={getBusinessConferences.state !== 'hasValue'}
          columns={columns}
          ToolbarComponent={ToolbarComponent}
          sortModel={sortModel}
          setGridSortModel={setGridSortModel}
          selected={selected}
        />
      </div>
      <DeleteConferenceModal
        handleClose={handleClose}
        show={show}
        userAtom={userAtom}
        setUserAtom={setUserAtom}
        setRefetchToggleForBusinessConferences={
          setRefetchToggleForBusinessConferences
        }
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        selected={selected}
      />
    </Paper>
  );
}
